// DashboardPage — org overview (tinted metric cards + recent updates). Header/subnav from OrgSettingsLayout.
import { useEffect, useState } from 'react';
import { useOrgPageMeta, useOrgSlug } from '@/layouts/orgSettingsMeta';
import { Spinner } from '@/components/ui';
import { billingService, organizationService } from '@/services/api';
import type { DashboardData } from '@/services/api/organizationService';
import OrgUpdatesFeed from '@/features/organization/OrgUpdatesFeed';
import OrgSectionHeading from '@/features/organization/OrgSectionHeading';

function formatUsdFromCents(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

export default function DashboardPage() {
  const slug = useOrgSlug();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingAmount, setBillingAmount] = useState('—');

  useEffect(() => {
    if (!slug) return;
    let active = true;
    organizationService
      .getDashboard(slug)
      .then((next) => {
        if (active) setData(next);
      })
      .catch(() => {
        if (active) setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    billingService
      .getBillingBody(slug)
      .then((body) => {
        if (!active) return;
        const cents = body.lastInvoice?.amount;
        setBillingAmount(cents == null ? '—' : formatUsdFromCents(cents));
      })
      .catch(() => {
        if (active) setBillingAmount('—');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useOrgPageMeta('Dashboard', 'Overview, seats, and recent activity.');

  if (loading) {
    return (
      <div className="org-tab-loading">
        <Spinner label="Loading dashboard" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="org-section">
        <div className="org-empty">Unable to load dashboard.</div>
      </div>
    );
  }

  return (
    <>
      <div className="org-metrics is-four">
        <div className="org-metric-card org-metric-card--navy">
          <span className="org-metric-icon" aria-hidden>
            <i className="bi bi-people" />
          </span>
          <div className="org-metric-value">{data.memberCount}</div>
          <div className="org-metric-label">Members</div>
        </div>
        <div className="org-metric-card org-metric-card--purple">
          <span className="org-metric-icon" aria-hidden>
            <i className="bi bi-hourglass-split" />
          </span>
          <div className="org-metric-value">{data.pendingMembers}</div>
          <div className="org-metric-label">Pending approvals</div>
        </div>
        <div className="org-metric-card org-metric-card--green">
          <span className="org-metric-icon" aria-hidden>
            <i className="bi bi-grid-3x3-gap" />
          </span>
          <div className="org-metric-value">
            {data.seatUsage} / {data.seatCap ?? '∞'}
          </div>
          <div className="org-metric-label">Seats used</div>
        </div>
        <div className="org-metric-card org-metric-card--orange">
          <span className="org-metric-icon" aria-hidden>
            <i className="bi bi-currency-dollar" />
          </span>
          <div className="org-metric-value">{billingAmount}</div>
          <div className="org-metric-label">Current monthly billing</div>
        </div>
      </div>

      <div className="org-panel">
        <OrgSectionHeading plain title="Recent updates" />
        <OrgUpdatesFeed rows={data.recentUpdates.slice(0, 5)} emptyMessage="No entries yet." />
      </div>
    </>
  );
}
